import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useChannel } from "./channel";
import "./shopee_workspace.css";

const ACTIVE = new Set(["BELUM_FIX", "REVISI"]);
const SHIPPED = new Set(["shipped", "completed", "to_return"]);
const NO_DL_TEXT = {
  not_synced: "Pesanan belum tersinkron",
  bad_number: "Nomor pesanan tidak cocok format Shopee",
  ambiguous: "Nomor pesanan ganda di beberapa toko",
  no_deadline: "Shopee belum memberi batas kirim",
};
const H = 3600000;

export const deadlineMs = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : null;
};

const fmtDur = (ms) => {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d} hari${h ? ` ${h} jam` : ""}`;
  if (h > 0) return `${h} jam${m ? ` ${m} menit` : ""}`;
  return `${m} menit`;
};

export const fmtWib = (t) => new Date(t).toLocaleString("id-ID", {
  timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
}) + " WIB";

// Perf — satu timer bersama untuk semua chip (bukan satu interval per kartu).
const subs = new Set();
let timer = null;
export const useMinuteTick = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    subs.add(setNow);
    if (!timer) timer = setInterval(() => { const t = Date.now(); subs.forEach(f => f(t)); }, 60000);
    return () => { subs.delete(setNow); if (!subs.size) { clearInterval(timer); timer = null; } };
  }, []);
  return now;
};

export function ShopeeDeadlineChip({ job, always = false }) {
  const now = useMinuteTick();
  const ch = useChannel();
  if (ch === "tokopedia") return null;
  if (!always && !ACTIVE.has(job.status)) return null;
  const tid = `shopee-card-deadline-${job.id}`;
  if (SHIPPED.has(job.shopee_order_status)) {
    return <span className="shopee-dl-chip is-none" data-testid={tid}>
      <Clock size={11} aria-hidden="true"/> Sudah dikirim di Shopee
    </span>;
  }
  const t = deadlineMs(job.shopee_deadline_iso);
  if (t === null) {
    return <span className="shopee-dl-chip is-none" data-testid={tid} data-reason={job.shopee_deadline_source || "none"}>
      <Clock size={11} aria-hidden="true"/> {NO_DL_TEXT[job.shopee_deadline_source] || "Batas kirim belum tersinkron"}
    </span>;
  }
  const diff = t - now;
  const tone = diff < 24 * H ? "red" : diff < 48 * H ? "yellow" : "green";
  const text = `Kirim Shopee · ${diff < 0 ? "Lewat" : "Sisa"} ${fmtDur(Math.abs(diff))}`;
  return <span className={`shopee-dl-chip is-${tone}`} data-testid={tid}
    title={`Batas kirim Shopee: ${fmtWib(t)}`}>
    <Clock size={11} aria-hidden="true"/> {text}
  </span>;
}

// Kartu Desain Bordir: batas kirim terakhir langsung di kartu (Shopee resmi · Tokopedia manual). Baca-saja.
export function BordirShipBy({ job }) {
  const now = useMinuteTick();
  const tkp = job.channel === "tokopedia";
  const tid = `bordir-shipby-${job.id}`;
  const t = deadlineMs(job.shopee_deadline_iso);
  if (t === null) return <div className="bsb is-none" data-testid={tid} data-level="none"><Clock size={13} aria-hidden="true"/> Batas kirim belum tersedia</div>;
  const diff = t - now;
  const shipped = !tkp && SHIPPED.has(job.shopee_order_status);
  const level = shipped ? "shipped" : diff < 0 ? "late" : diff < 24 * H ? "near" : diff < 48 * H ? "soon" : "ok";
  const tag = { shipped: "Sudah dikirim", late: "Terlambat", near: `Mendekati batas · sisa ${fmtDur(diff)}`, soon: `Sisa ${fmtDur(diff)}`, ok: `Sisa ${fmtDur(diff)}` }[level];
  return <div className={`bsb is-${level}`} data-testid={tid} data-level={level}>
    <Clock size={13} aria-hidden="true"/>
    <span>Batas Kirim Terakhir{tkp ? " (manual)" : ""}: <b data-testid={`${tid}-at`}>{fmtWib(t)}</b></span>
    <span className="bsb-tag" data-testid={`${tid}-tag`}>{tag}</span>
  </div>;
}
